import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check, AlertCircle, Search, X, Calendar, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import Header from '@/components/Header';
import ProgressBar from '@/components/ProgressBar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getProducts, getProductDetail, getPublicSettings } from '@/lib/api';
import { cn } from '@/lib/utils';

// Category tree structure - based on actual Shopify product_types
const CATEGORY_TREE = {
  'DONNA': [
    { key: 'abiti', label: 'Abiti', match: ['Abito Donna', 'Tubino/Vestito', 'Tutina'] },
    { key: 'sposa', label: 'Sposa', match: ['Abito da Sposa'] },
    { key: 'capispalla', label: 'Capispalla', match: ['Capispalla Donna'] },
    { key: 'scarpe', label: 'Scarpe', match: ['Décolleté', 'Sandali'] }
  ],
  'UOMO': [
    { key: 'abiti', label: 'Abiti', match: ['Abito Uomo'] },
    { key: 'capispalla', label: 'Capispalla', match: ['Cappotto Uomo'] },
    { key: 'pantaloni', label: 'Pantaloni', match: ['Pantaloni'] },
    { key: 'scarpe', label: 'Scarpe', match: ['Mocassino'] },
    { key: 'accessori', label: 'Accessori', match: ['Cravatta e Pochette', 'Cintura'] }
  ]
};

const PRODUCTS_PER_PAGE = 12;

const SelectExchange = () => {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [reason, setReason] = useState('');
  const [sizeSelections, setSizeSelections] = useState({});
  const [productSelections, setProductSelections] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);

  // Product detail for current item (fetched individually)
  const [currentProductDetail, setCurrentProductDetail] = useState(null);
  const [productDetailLoading, setProductDetailLoading] = useState(false);

  // Catalog browsing state
  const [browsingCatalog, setBrowsingCatalog] = useState(false);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [quickChangeOpen, setQuickChangeOpen] = useState(true);

  // Navigation state
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState('abiti');
  const [visibleProducts, setVisibleProducts] = useState(PRODUCTS_PER_PAGE);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [eventDate, setEventDate] = useState('');
  const [exchangeUnavailableEnabled, setExchangeUnavailableEnabled] = useState(true);

  // Initial load: order data + single product detail
  useEffect(() => {
    const loadData = async () => {
      const storedOrder = sessionStorage.getItem('currentOrder');
      const storedItems = sessionStorage.getItem('selectedItems');
      const storedReason = sessionStorage.getItem('reason');

      if (storedOrder && storedItems) {
        const parsedOrder = JSON.parse(storedOrder);
        const parsedItems = JSON.parse(storedItems);
        setOrder(parsedOrder);
        setSelectedItems(parsedItems);
        if (storedReason) setReason(storedReason);

        try {
          const pubSettings = await getPublicSettings();
          setExchangeUnavailableEnabled(pubSettings.exchange_unavailable_enabled !== false);
        } catch {}

        // Fetch only the current item's product detail
        const firstItem = parsedItems[0];
        if (firstItem?.id) {
          setProductDetailLoading(true);
          try {
            const detail = await getProductDetail(firstItem.id);
            setCurrentProductDetail(detail);
          } catch {
            // Non-critical: quick change just won't show
          }
          setProductDetailLoading(false);
        }
      } else {
        navigate('/');
      }
      setIsLoading(false);
    };

    loadData();
  }, [navigate]);

  // When currentItemIndex changes, fetch the new product detail
  useEffect(() => {
    if (!selectedItems.length) return;
    const item = selectedItems[currentItemIndex];
    if (!item?.id) return;

    // If we already have this product loaded, skip
    if (currentProductDetail && String(currentProductDetail.id) === String(item.id)) return;

    const fetchDetail = async () => {
      setProductDetailLoading(true);
      try {
        const detail = await getProductDetail(item.id);
        setCurrentProductDetail(detail);
      } catch {}
      setProductDetailLoading(false);
    };
    fetchDetail();
  }, [currentItemIndex, selectedItems]);

  const currentItem = selectedItems[currentItemIndex];

  // Get available variants from the fetched product detail
  const availableVariants = useMemo(() => {
    if (!currentItem || !currentProductDetail) return [];
    if (String(currentProductDetail.id) !== String(currentItem.id)) return [];
    return (currentProductDetail.variants || []).filter(
      v => v.available && String(v.id) !== String(currentItem.variant_id)
    );
  }, [currentItem, currentProductDetail]);

  // Fetch catalog when user selects a collection
  const loadCatalog = useCallback(async (collection) => {
    setCatalogLoading(true);
    setCatalogLoaded(false);
    try {
      const data = await getProducts({ collection });
      setCatalogProducts(data);
      setCatalogLoaded(true);
    } catch {
      toast.error('Errore nel caricamento del catalogo');
    }
    setCatalogLoading(false);
  }, []);

  const handleCollectionChange = (collection) => {
    setSelectedCollection(collection);
    setSelectedSubcategory('abiti');
    setVisibleProducts(PRODUCTS_PER_PAGE);
    setSearchQuery('');
    setShowSearch(false);
    setBrowsingCatalog(true);
    setQuickChangeOpen(false);
    loadCatalog(collection);
  };

  const handleSubcategoryChange = (subcatKey) => {
    setSelectedSubcategory(subcatKey);
    setVisibleProducts(PRODUCTS_PER_PAGE);
  };

  // Get current subcategory object
  const currentSubcategoryObj = selectedCollection
    ? CATEGORY_TREE[selectedCollection]?.find(c => c.key === selectedSubcategory)
    : null;

  // Filter products based on subcategory and search
  const filteredProducts = useMemo(() => {
    let filtered = catalogProducts;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const allSubcategories = CATEGORY_TREE[selectedCollection] || [];
      const allMatchTypes = allSubcategories.flatMap(s => s.match);

      filtered = filtered.filter(p => {
        const productType = p.product_type || '';
        const title = (p.title || '').toLowerCase();
        const inCollection = allMatchTypes.some(m => productType === m);
        const matchesSearch = title.includes(query) || productType.toLowerCase().includes(query);
        return inCollection && matchesSearch;
      });
    } else if (currentSubcategoryObj) {
      filtered = filtered.filter(p => {
        const productType = p.product_type || '';
        return currentSubcategoryObj.match.some(m => productType === m);
      });
    }

    return filtered;
  }, [catalogProducts, currentSubcategoryObj, searchQuery, selectedCollection]);

  const displayedProducts = filteredProducts.slice(0, visibleProducts);
  const hasMoreProducts = visibleProducts < filteredProducts.length;

  // Subcategory counts
  const subcategoryCounts = useMemo(() => {
    if (!selectedCollection) return {};
    const counts = {};
    CATEGORY_TREE[selectedCollection]?.forEach(subcat => {
      counts[subcat.key] = catalogProducts.filter(p => {
        const productType = p.product_type || '';
        return subcat.match.some(m => productType === m);
      }).length;
    });
    return counts;
  }, [catalogProducts, selectedCollection]);

  const handleSizeChange = (variantId) => {
    const variant = currentProductDetail?.variants?.find(v => String(v.id) === String(variantId));
    setSizeSelections(prev => ({
      ...prev,
      [currentItem.unique_id]: {
        product_id: currentItem.id,
        variant_id: variantId,
        title: currentProductDetail?.title,
        variant_title: variant?.title,
        price: variant?.price,
        image_url: currentProductDetail?.image_url
      }
    }));
    setProductSelections(prev => {
      const newState = { ...prev };
      delete newState[currentItem.unique_id];
      return newState;
    });
  };

  const handleProductSelect = (product, variant) => {
    const isOutOfStock = !variant.available || variant.inventory_quantity <= 0;
    setProductSelections(prev => ({
      ...prev,
      [currentItem.unique_id]: {
        product_id: product.id,
        variant_id: variant.id,
        title: product.title,
        variant_title: variant.title,
        price: variant.price,
        image_url: product.image_url,
        out_of_stock: isOutOfStock
      }
    }));
    setSizeSelections(prev => {
      const newState = { ...prev };
      delete newState[currentItem.unique_id];
      return newState;
    });
  };

  const hasUnavailableSelection = useMemo(() => {
    return selectedItems.some(item => {
      const sel = productSelections[item.unique_id] || sizeSelections[item.unique_id];
      return sel?.out_of_stock;
    });
  }, [selectedItems, productSelections, sizeSelections]);

  const getExchangeSelection = (itemUniqueId) => {
    return sizeSelections[itemUniqueId] || productSelections[itemUniqueId];
  };

  const isCurrentItemComplete = () => !!getExchangeSelection(currentItem?.unique_id);

  const allItemsComplete = () => selectedItems.every(item => getExchangeSelection(item.unique_id));

  const goToNextItem = () => {
    if (currentItemIndex < selectedItems.length - 1) {
      setCurrentItemIndex(currentItemIndex + 1);
      // Reset catalog browsing for next item
      setBrowsingCatalog(false);
      setQuickChangeOpen(true);
      setCatalogLoaded(false);
      setSelectedCollection(null);
    }
  };

  const handleSubmit = () => {
    if (!allItemsComplete()) {
      toast.error('Seleziona un prodotto sostitutivo per ogni articolo');
      return;
    }
    if (hasUnavailableSelection && !eventDate) {
      toast.error('Inserisci la data del tuo evento per i prodotti non disponibili');
      return;
    }

    const exchangeItems = selectedItems.map(item => {
      const selection = getExchangeSelection(item.unique_id);
      if (!selection) return null;
      return {
        product_id: String(selection.product_id),
        variant_id: String(selection.variant_id),
        title: selection.title,
        variant_title: selection.variant_title,
        quantity: 1,
        price: String(selection.price),
        image_url: selection.image_url || null,
        out_of_stock: selection.out_of_stock || false
      };
    }).filter(Boolean);

    sessionStorage.setItem('exchangeItems', JSON.stringify(exchangeItems));
    if (hasUnavailableSelection) {
      sessionStorage.setItem('eventDate', eventDate);
      sessionStorage.setItem('hasUnavailableItems', 'true');
    } else {
      sessionStorage.removeItem('eventDate');
      sessionStorage.removeItem('hasUnavailableItems');
    }
    navigate(`/order/${orderNumber}/summary`);
  };

  const handleLoadMore = () => {
    setVisibleProducts(prev => prev + PRODUCTS_PER_PAGE);
  };

  if (!order || isLoading) {
    return (
      <div className="page-container bg-white">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Caricamento...</p>
          </div>
        </main>
      </div>
    );
  }

  const currentSelection = getExchangeSelection(currentItem?.unique_id);

  return (
    <div className="page-container bg-white">
      <Header showBack backTo={`/order/${orderNumber}/type`} />
      <ProgressBar currentStep={3} totalSteps={5} />

      <main className="flex-1 py-6 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-6"
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-2">
              Passaggio 3 di 5
            </p>
            <h1 className="font-heading text-2xl md:text-3xl font-normal tracking-tight mb-1">
              Scegli il Cambio
            </h1>
            <p className="text-muted-foreground text-sm">
              Articolo {currentItemIndex + 1} di {selectedItems.length}
            </p>
          </motion.div>

          {/* Current Item Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="border border-border p-4 mb-6 bg-zinc-50"
          >
            <div className="flex gap-4">
              <div className="w-20 h-28 bg-white overflow-hidden flex-shrink-0 border">
                {currentItem?.image_url && (
                  <img src={currentItem.image_url} alt={currentItem.title} className="w-full h-full object-cover" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-heading text-base md:text-lg truncate">{currentItem?.title}</h4>
                <p className="text-sm text-muted-foreground">{currentItem?.variant_title}</p>
                <p className="text-sm font-medium mt-1">&euro;{parseFloat(currentItem?.price || 0).toFixed(2)}</p>
              </div>
            </div>
          </motion.div>

          {/* SECTION 1: Quick Size/Color Change — collapsible */}
          {(availableVariants.length > 0 || productDetailLoading) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="border border-border mb-6 overflow-hidden"
            >
              <button
                onClick={() => setQuickChangeOpen(!quickChangeOpen)}
                className="w-full flex items-center justify-between p-4 md:p-6 hover:bg-zinc-50 transition-colors"
                data-testid="quick-change-toggle"
              >
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground">
                  Cambio Taglia/Colore Veloce
                </p>
                {quickChangeOpen ? (
                  <ChevronUp className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-muted-foreground" />
                )}
              </button>
              <AnimatePresence>
                {quickChangeOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 md:px-6 pb-4 md:pb-6">
                      {productDetailLoading ? (
                        <div className="flex items-center justify-center py-6">
                          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                          <span className="ml-2 text-sm text-muted-foreground">Caricamento varianti...</span>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                          {availableVariants.map(variant => (
                            <button
                              key={variant.id}
                              onClick={() => handleSizeChange(variant.id)}
                              className={cn(
                                "p-3 border text-left text-sm transition-all",
                                sizeSelections[currentItem?.unique_id]?.variant_id === variant.id
                                  ? "border-primary bg-primary text-white"
                                  : "border-border hover:border-primary bg-white"
                              )}
                              data-testid={`quick-variant-${variant.id}`}
                            >
                              <p className="font-medium truncate">{variant.title}</p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {/* Divider */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground uppercase tracking-wide whitespace-nowrap">
              oppure scegli dal catalogo
            </span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* SECTION 2: Collection Buttons (DONNA | UOMO) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-4"
          >
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleCollectionChange('DONNA')}
                className={cn(
                  "py-4 text-center font-bold tracking-[0.15em] uppercase border-2 transition-all",
                  selectedCollection === 'DONNA'
                    ? "border-primary bg-primary text-white"
                    : "border-border bg-white hover:border-primary"
                )}
                data-testid="collection-donna"
              >
                DONNA
              </button>
              <button
                onClick={() => handleCollectionChange('UOMO')}
                className={cn(
                  "py-4 text-center font-bold tracking-[0.15em] uppercase border-2 transition-all",
                  selectedCollection === 'UOMO'
                    ? "border-primary bg-primary text-white"
                    : "border-border bg-white hover:border-primary"
                )}
                data-testid="collection-uomo"
              >
                UOMO
              </button>
            </div>
          </motion.div>

          {/* Catalog loading spinner */}
          {catalogLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <span className="ml-3 text-sm text-muted-foreground">Caricamento catalogo...</span>
            </div>
          )}

          {/* Catalog content — only shown after collection selected and loaded */}
          {catalogLoaded && !catalogLoading && selectedCollection && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Subcategory Navigation */}
              <div className="mb-4">
                <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
                  {CATEGORY_TREE[selectedCollection]?.map(subcat => {
                    const count = subcategoryCounts[subcat.key] || 0;
                    if (count === 0) return null;
                    return (
                      <button
                        key={subcat.key}
                        onClick={() => handleSubcategoryChange(subcat.key)}
                        className={cn(
                          "px-4 py-2 text-sm font-medium border whitespace-nowrap transition-all flex-shrink-0",
                          selectedSubcategory === subcat.key
                            ? "border-primary bg-primary text-white"
                            : "border-border bg-white hover:border-primary"
                        )}
                        data-testid={`subcategory-${subcat.key}`}
                      >
                        {subcat.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Products Count + Search */}
              <div className="flex items-center justify-between mb-4 gap-3">
                <p className="text-sm text-muted-foreground flex-shrink-0" data-testid="products-count">
                  {filteredProducts.length} prodotti
                </p>
                <div className="flex-1 max-w-xs">
                  {showSearch ? (
                    <div className="relative">
                      <Input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Cerca prodotto..."
                        className="rounded-none pr-10 h-10"
                        autoFocus
                        data-testid="search-input"
                      />
                      <button
                        onClick={() => { setSearchQuery(''); setShowSearch(false); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-zinc-100 rounded"
                      >
                        <X className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowSearch(true)}
                      className="flex items-center gap-2 text-sm border border-border px-4 py-2 hover:border-primary transition-colors ml-auto"
                      data-testid="search-btn"
                    >
                      <Search className="w-4 h-4" />
                      Cerca
                    </button>
                  )}
                </div>
              </div>

              {/* Product Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {displayedProducts.map(product => {
                  const allVariants = product.variants;
                  const availVars = allVariants.filter(v => v.available);
                  const unavailableVariants = allVariants.filter(v => !v.available);
                  const isSelected = productSelections[currentItem?.unique_id]?.product_id === product.id;
                  const selectedIsOutOfStock = productSelections[currentItem?.unique_id]?.out_of_stock;
                  const isFullyOutOfStock = availVars.length === 0;

                  if (!exchangeUnavailableEnabled && isFullyOutOfStock) return null;

                  return (
                    <motion.div
                      key={product.id}
                      className={cn(
                        "border transition-all group bg-white",
                        isSelected && !selectedIsOutOfStock ? "border-primary ring-2 ring-primary/20" : "",
                        isSelected && selectedIsOutOfStock ? "border-orange-500 ring-2 ring-orange-200" : "",
                        !isSelected ? "border-border hover:border-primary" : ""
                      )}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      data-testid={`product-${product.id}`}
                    >
                      <div className="aspect-[3/4] bg-secondary overflow-hidden relative">
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={product.title}
                            className={cn(
                              "w-full h-full object-cover group-hover:scale-105 transition-transform duration-500",
                              isFullyOutOfStock && "opacity-60"
                            )}
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                            No Image
                          </div>
                        )}
                        {isSelected && (
                          <div className={cn(
                            "absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center",
                            selectedIsOutOfStock ? "bg-orange-500" : "bg-primary"
                          )}>
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                        {exchangeUnavailableEnabled && isFullyOutOfStock && !isSelected && (
                          <div className="absolute top-2 left-2 px-2 py-1 bg-orange-500/90 text-white text-[10px] font-bold uppercase tracking-wider">
                            Non disponibile
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <h4 className="font-medium text-sm leading-tight line-clamp-2 mb-1">{product.title}</h4>
                        <p className="text-xs text-muted-foreground mb-2">{product.product_type}</p>

                        <Select
                          value={isSelected ? String(productSelections[currentItem?.unique_id]?.variant_id || '') : ''}
                          onValueChange={(variantId) => {
                            const variant = allVariants.find(v => String(v.id) === String(variantId));
                            if (variant) handleProductSelect(product, variant);
                          }}
                        >
                          <SelectTrigger className="rounded-none h-9 text-xs">
                            <SelectValue placeholder="Seleziona taglia" />
                          </SelectTrigger>
                          <SelectContent>
                            {availVars.map(variant => (
                              <SelectItem key={variant.id} value={String(variant.id)}>
                                {variant.title} - &euro;{parseFloat(variant.price).toFixed(0)}
                              </SelectItem>
                            ))}
                            {exchangeUnavailableEnabled && unavailableVariants.length > 0 && availVars.length > 0 && (
                              <div className="px-2 py-1 text-[10px] text-orange-600 uppercase tracking-wider font-bold border-t">
                                Non disponibili
                              </div>
                            )}
                            {exchangeUnavailableEnabled && unavailableVariants.map(variant => (
                              <SelectItem key={variant.id} value={String(variant.id)} className="text-orange-600">
                                {variant.title} - &euro;{parseFloat(variant.price).toFixed(0)} (non disp.)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {filteredProducts.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <p>Nessun prodotto disponibile in questa categoria</p>
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="text-primary underline mt-2 text-sm"
                    >
                      Cancella ricerca
                    </button>
                  )}
                </div>
              )}

              {/* Load More Button */}
              {hasMoreProducts && (
                <div className="text-center mt-8">
                  <Button
                    onClick={handleLoadMore}
                    variant="outline"
                    className="rounded-none px-8"
                    data-testid="load-more-btn"
                  >
                    Carica altro ({filteredProducts.length - visibleProducts} rimanenti)
                  </Button>
                </div>
              )}
            </motion.div>
          )}

          {/* Prompt to choose collection if not browsing yet */}
          {!selectedCollection && !browsingCatalog && (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">Seleziona una categoria per esplorare il catalogo</p>
            </div>
          )}

          {/* Out-of-stock warning + Event Date */}
          <AnimatePresence>
            {hasUnavailableSelection && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="border border-orange-300 bg-orange-50 p-4 md:p-5 mt-6"
              >
                <div className="flex items-start gap-3 mb-4">
                  <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-orange-900">Prodotto attualmente non disponibile</p>
                    <p className="text-xs text-orange-700 mt-1">
                      Hai selezionato un prodotto non attualmente disponibile in magazzino.
                      La richiesta potrebbe essere rifiutata nel caso in cui non fosse possibile
                      rispettare la data del tuo evento. Verrà accettata solo se lo staff riterrà
                      di poter rispettare le tempistiche necessarie.
                    </p>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold tracking-[0.1em] uppercase text-orange-800 mb-2 block">
                    Data del tuo evento *
                  </label>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-orange-500" />
                    <input
                      type="date"
                      value={eventDate}
                      onChange={(e) => setEventDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="px-3 py-2 border border-orange-300 bg-white text-sm w-full max-w-xs focus:outline-none focus:ring-2 focus:ring-orange-400"
                      data-testid="event-date-input"
                    />
                  </div>
                  <p className="text-[11px] text-orange-600 mt-2">
                    Dopo l'approvazione, riceverai una nota con la data prevista di produzione dell'abito.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Selection Summary - Fixed Bottom on Mobile */}
          <AnimatePresence>
            {currentSelection && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="fixed bottom-0 left-0 right-0 bg-white border-t border-border p-4 shadow-lg md:relative md:mt-8 md:border md:shadow-none z-50"
              >
                <div className="max-w-6xl mx-auto">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-emerald-900 truncate">Selezionato</p>
                        <p className="text-xs text-emerald-700 truncate">{currentSelection.title} - {currentSelection.variant_title}</p>
                      </div>
                    </div>

                    {currentItemIndex < selectedItems.length - 1 ? (
                      <Button
                        onClick={goToNextItem}
                        disabled={!isCurrentItemComplete()}
                        className="luxury-btn-primary whitespace-nowrap"
                      >
                        Avanti
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    ) : (
                      <Button
                        onClick={handleSubmit}
                        disabled={!allItemsComplete()}
                        className="luxury-btn-primary whitespace-nowrap"
                        data-testid="submit-exchange-btn"
                      >
                        Riepilogo
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Spacer for fixed bottom bar */}
          {currentSelection && <div className="h-24 md:h-0" />}
        </div>
      </main>
    </div>
  );
};

export default SelectExchange;
